import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { Plus, Printer, Check, Trash2, Receipt } from "lucide-react";
import { toast } from "sonner";
import { formatFCFA } from "../lib/fcfa";
import { useAuth } from "../contexts/AuthContext";
import { printPayslip } from "../lib/documents";

const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

export default function Payslips() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const today = new Date();
  const [form, setForm] = useState({
    employee_id: "", month: today.getMonth() + 1, year: today.getFullYear(),
    base_salary: "", bonuses: "0", deductions: "0",
    cnss_employee: "", cnss_employer: "", irpp: "", notes: "",
  });

  const load = () => api.get("/payslips").then(({ data }) => setList(data));
  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data));
  }, []);

  const onEmpChange = (id) => {
    const e = employees.find(x => x.id === id);
    if (e) {
      const base = e.salary;
      setForm(f => ({
        ...f, employee_id: id, base_salary: String(base),
        cnss_employee: String(Math.round(base * 0.036)),
        cnss_employer: String(Math.round(base * 0.164)),
        irpp: String(Math.round(base * 0.05)),
      }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payslips", {
        ...form,
        month: parseInt(form.month, 10), year: parseInt(form.year, 10),
        base_salary: parseInt(form.base_salary, 10),
        bonuses: parseInt(form.bonuses || 0, 10),
        deductions: parseInt(form.deductions || 0, 10),
        cnss_employee: parseInt(form.cnss_employee || 0, 10),
        cnss_employer: parseInt(form.cnss_employer || 0, 10),
        irpp: parseInt(form.irpp || 0, 10),
      });
      toast.success("Bulletin généré");
      setOpen(false); load();
    } catch (err) { toast.error("Erreur"); }
  };

  const markPaid = async (id) => {
    await api.patch(`/payslips/${id}/pay`);
    toast.success("Marqué comme payé");
    load();
  };

  const onDel = async (id) => {
    if (!confirm("Supprimer ce bulletin ?")) return;
    await api.delete(`/payslips/${id}`);
    load();
  };

  const onPrint = (ps) => {
    const emp = employees.find(e => e.id === ps.employee_id);
    if (emp) printPayslip(user.club, emp, ps);
  };

  return (
    <div>
      <PageHeader
        overline="Paie"
        title="Bulletins de paie"
        description="Génération automatique CNSS, IRPP et net à payer en FCFA."
        actions={
          <Button onClick={() => setOpen(true)} data-testid="create-payslip-btn"
            className="btn-club" disabled={employees.length === 0}>
            <Plus className="w-4 h-4 mr-2" /> Nouveau bulletin
          </Button>
        }
      />

      {employees.length === 0 && (
        <div className="bg-[#FBC02D]/10 border border-[#FBC02D]/30 text-[#121212] rounded-md p-4 mb-4 text-sm">
          Ajoutez d'abord des employés dans la page « Employés » pour pouvoir générer des bulletins.
        </div>
      )}

      <div className="bg-white border border-[#E0E0E0] rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="payslips-table">
            <thead className="bg-[#F4F5F7] text-[#616161] tracking-overline text-[10px]">
              <tr>
                <th className="text-left px-4 py-3">Employé</th>
                <th className="text-left px-4 py-3">Période</th>
                <th className="text-right px-4 py-3">Brut</th>
                <th className="text-right px-4 py-3">Cotisations</th>
                <th className="text-right px-4 py-3">Net</th>
                <th className="text-left px-4 py-3">État</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E0E0E0]">
              {list.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#616161]">Aucun bulletin.</td></tr>
              ) : list.map((p) => (
                <tr key={p.id} className="hover:bg-[#F4F5F7]/50" data-testid={`payslip-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium">{p.employee_name}</td>
                  <td className="px-4 py-3">{MONTHS[p.month - 1]} {p.year}</td>
                  <td className="px-4 py-3 text-right font-mono">{formatFCFA(p.base_salary + p.bonuses)}</td>
                  <td className="px-4 py-3 text-right font-mono text-[#C62828]">− {formatFCFA(p.cnss_employee + p.irpp + p.deductions)}</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-[#2E7D32]">{formatFCFA(p.net_salary)}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className={p.is_paid ? "bg-[#2E7D32]/10 text-[#2E7D32] border-[#2E7D32]/30" : "bg-[#F57F17]/10 text-[#F57F17] border-[#F57F17]/30"}>
                      {p.is_paid ? "Payé" : "En attente"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => onPrint(p)} title="Imprimer" data-testid={`print-payslip-${p.id}`}><Printer className="w-4 h-4" /></Button>
                    {!p.is_paid && <Button size="sm" variant="ghost" onClick={() => markPaid(p.id)} title="Marquer payé"><Check className="w-4 h-4 text-[#2E7D32]" /></Button>}
                    <Button size="sm" variant="ghost" onClick={() => onDel(p.id)}><Trash2 className="w-4 h-4 text-[#C62828]" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payslip-dialog">
          <DialogHeader><DialogTitle className="font-display">Nouveau bulletin</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>Employé *</Label>
              <Select value={form.employee_id} onValueChange={onEmpChange}>
                <SelectTrigger data-testid="ps-employee"><SelectValue placeholder="Choisir" /></SelectTrigger>
                <SelectContent>
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name} — {formatFCFA(e.salary)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Mois *</Label>
                <Select value={String(form.month)} onValueChange={(v) => setForm({ ...form, month: v })}>
                  <SelectTrigger data-testid="ps-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Année *</Label><Input type="number" required value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} data-testid="ps-year" /></div>
              <div><Label>Salaire brut</Label><Input type="number" required value={form.base_salary} onChange={(e) => setForm({ ...form, base_salary: e.target.value })} data-testid="ps-base" /></div>
              <div><Label>Primes</Label><Input type="number" value={form.bonuses} onChange={(e) => setForm({ ...form, bonuses: e.target.value })} /></div>
              <div><Label>Retenues</Label><Input type="number" value={form.deductions} onChange={(e) => setForm({ ...form, deductions: e.target.value })} /></div>
              <div><Label>CNSS salarié</Label><Input type="number" value={form.cnss_employee} onChange={(e) => setForm({ ...form, cnss_employee: e.target.value })} /></div>
              <div><Label>CNSS patronal</Label><Input type="number" value={form.cnss_employer} onChange={(e) => setForm({ ...form, cnss_employer: e.target.value })} /></div>
              <div><Label>IRPP</Label><Input type="number" value={form.irpp} onChange={(e) => setForm({ ...form, irpp: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-club" data-testid="payslip-submit-btn">Générer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
